const debounce = require('lodash/debounce');
const { isFunction } = require('../../type');
const createStorageLens = require('./createStorageLens');
const { createInMemoryStorage } = require('../storages/inMemoryStorage');
const { default: createCache } = require('../../cache');
const storagePlainObjectTransformer = require('../transformers/storagePlainObjectTransformer');
const {
  executeWithPurgeAndRetryIfQuotaExceeded,
} = require('../errors/QuotaExceededError');
const purgeStorage = require('../utils/purgeStorage');
const { isItemExpired } = require('../decorators/ttlDecorator');

/**
 * Creates a StorageLens that maps a logical storage into a single entry
 * of an underlying inner storage.
 *
 * The logical storage is serialized as JSON and stored under a single key
 * in the inner storage. When read, the JSON is deserialized into an
 * in‑memory storage instance. Writes are debounced to reduce the number
 * of physical storage operations.
 *
 * @param {string} innerStorageKey
 * The key under which the logical storage is serialized.
 *
 * @returns {StorageLens}
 * A lens that exposes a full logical storage backed by a single entry
 * in the inner storage.
 */
function createStorageLensForSingleEntryOfStorage(
  innerStorageKey,
  { purgeFn = () => {} } = {}
) {
  const cachedStorage = createCache(3000);

  /**
   * Reads the logical storage from the inner storage.
   *
   * @param {Storage} innerStorage
   * The underlying storage where the serialized logical storage is stored.
   *
   * @returns {Storage}
   * A logical in‑memory storage reconstructed from the serialized data.
   */
  function getter(innerStorage) {
    let storage = cachedStorage.get();
    if (!storage) {
      const stringifiedStorage = innerStorage.getItem(innerStorageKey);
      if (stringifiedStorage === null) {
        storage = createInMemoryStorage();
      } else {
        try {
          storage = createInMemoryStorage(JSON.parse(stringifiedStorage));
        } catch (e) {
          // Losing stored data is not routine: warn, so it is visible among
          // the console noise of a normal page.
          console.warn(
            `The key ${innerStorageKey} contains invalid data and will be reset`,
            stringifiedStorage
          );
          storage = createInMemoryStorage();
        }
      }
      cachedStorage.set(storage);
    }
    return storage;
  }

  /**
   * Debounced write of the logical storage into the inner storage.
   *
   * @param {Storage} storage
   * The logical storage to serialize.
   *
   * @param {Storage} innerStorage
   * The underlying storage where the serialized data is written.
   */
  const debouncedSetter = debounce((storage, innerStorage) => {
    if (storage === null) {
      innerStorage.removeItem(innerStorageKey);
    } else {
      const writeOperation = () =>
        innerStorage.setItem(
          innerStorageKey,
          JSON.stringify(storagePlainObjectTransformer.toObject(storage))
        );
      if (isFunction(purgeFn)) {
        executeWithPurgeAndRetryIfQuotaExceeded({
          operation: writeOperation,
          purge: () => {
            // eslint-disable-next-line no-param-reassign
            storage = purgeStorage(
              isItemExpired, // If the logic storage has TTL decorator applied
              storage
            );
          },
        });
      } else {
        writeOperation();
      }
    }
    cachedStorage.clear();
  }, 300);

  /**
   * Writes the logical storage into the inner storage.
   *
   * @param {Storage} storage
   * The logical storage to persist.
   *
   * @param {Storage} innerStorage
   * The underlying storage where the serialized data is saved.
   *
   * @returns {Storage}
   * The same logical storage instance.
   */
  function setter(storage, innerStorage) {
    const isStorageSupported =
      (isFunction(storage?.getItem) && isFunction(storage?.setItem)) ||
      storage === null;
    if (!isStorageSupported) {
      throw new TypeError('Given storage is not supported by the setter');
    }

    cachedStorage.set(storage);
    debouncedSetter(storage, innerStorage);
    return storage;
  }

  return createStorageLens(getter, setter);
}

module.exports = createStorageLensForSingleEntryOfStorage;
